/**
 * Los nombres de las categorías, para poder escribir "en Relojería" y no "en 1 categoría".
 *
 * *** Se piden sólo cuando hacen falta. *** `/catalogo/categorias` le pega a la API de
 * Tienda Nube, y ese rate limit es **compartido con todas las apps** que el comerciante
 * tenga instaladas: si lo gastamos nosotros, le rompemos la sincronización con Mercado
 * Libre o con su ERP. Una tienda que sólo hizo corridas sobre todo el catálogo no tiene
 * por qué pagar esa llamada al abrir la app.
 *
 * La llamada en sí está cacheada por sesión en `api.categorias`, así que el wizard, el
 * historial y el modal de deshacer comparten una sola.
 */

import { useEffect, useState } from 'react';
import { api, type Corrida } from './api';
import { describirRegla } from '@precios/rules/describir';

/**
 * `null` mientras no se pidieron o no llegaron.
 *
 * Quien lo use tiene que funcionar sin esto: la descripción sale igual, con el conteo. No
 * se espera a que lleguen para mostrar la pantalla, y si la llamada falla no se molesta al
 * comerciante — un "en 3 categorías" no es un error.
 */
export function useNombresDeCategorias(haceFalta: boolean): ReadonlyMap<number, string> | null {
  const [categorias, setCategorias] = useState<ReadonlyMap<number, string> | null>(null);

  useEffect(() => {
    if (!haceFalta || categorias !== null) return;

    let vigente = true;
    void api
      .categorias()
      .then(({ categorias: lista }) => {
        if (!vigente) return;
        setCategorias(new Map(lista.map((c) => [c.id, c.ruta === '' ? c.nombre : c.ruta])));
      })
      .catch(() => undefined);
    return () => {
      vigente = false;
    };
  }, [haceFalta, categorias]);

  return categorias;
}

/** Si alguna de estas corridas se describe mejor con los nombres a mano. */
export function algunaAcotaPorCategoria(corridas: readonly Corrida[]): boolean {
  return corridas.some((corrida) => (corrida.regla?.spec?.scope.categoryIds.length ?? 0) > 0);
}

/**
 * La regla de una corrida en una línea, o `null` si no tiene ninguna.
 *
 * Con `spec: null` —una regla que el backend no pudo leer con el esquema de hoy— queda el
 * nombre, que ya identifica algo.
 */
export function descripcionDeRegla(
  corrida: Corrida,
  categorias: ReadonlyMap<number, string> | null,
): string | null {
  const regla = corrida.regla;
  if (regla === null) return null;
  if (regla.spec === null) return regla.nombre;
  return describirRegla(regla.spec, categorias === null ? {} : { categorias });
}
